/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { focusBoxShadow, Theme, useTheme, VFlow } from 'bold-ui'
import { format } from 'date-fns'
import { OrigemDadoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useMemo, useRef } from 'react'
import { AccordionItem, AccordionItemPanel, AccordionItemState } from 'react-accessible-accordion'

import { useHistoricoPrint } from '../../hooks/useHistoricoPrint'
import { CidadaoHistorico, HistoricoAtendimentoModel, IdadeOrigemAtendimento } from '../../model/model-historico'
import { getSubtipoAtendimento, isVisitaAusenteOuRecusada } from '../../utils/utils-historico'
import { HistoricoAccordionItemObservacaoWrapper } from './detail/atendimento-observacao/HistoricoAccordionItemObservacaoWrapper'
import HistoricoDetalhesPanel from './detail/HistoricoDetalhesPanel'
import HistoricoAccordionHeader from './header/HistoricoAccordionHeader'

export interface HistoricoAccordionProps {
  historicoAtendimento: HistoricoAtendimentoModel
  cidadao: CidadaoHistorico
  refetchOnListagem(): void
  checked: boolean
  onSelectToPrint?(checked: boolean, historico: HistoricoAtendimentoModel): void
  historicoPrintProps: ReturnType<typeof useHistoricoPrint>
}

export default function HistoricoAccordion(props: HistoricoAccordionProps) {
  const { historicoAtendimento, cidadao, refetchOnListagem, checked, onSelectToPrint, historicoPrintProps } = props
  const { analytics } = useFirebase()
  const isAccordionClosedRef = useRef(true)
  const theme = useTheme()
  const dataAtendimento = format(historicoAtendimento.dataAtendimento, 'yyyy-MM-dd')
  const origemAtendimento: OrigemDadoEnum = historicoAtendimento.origemAtendimento
  const idadeOrigemAtendimento: IdadeOrigemAtendimento = {
    dataAtendimento,
    dataNascimentoCidadao: cidadao.dataNascimento,
    origemAtendimento,
  }

  const subtipoAtendimento = getSubtipoAtendimento(historicoAtendimento)

  const isAccordionDisabled = isVisitaAusenteOuRecusada(historicoAtendimento) || historicoAtendimento.isInativo

  const classes = useMemo(() => styles(theme, isAccordionDisabled), [isAccordionDisabled, theme])

  return (
    <HistoricoAccordionItemObservacaoWrapper historicoAtendimento={historicoAtendimento}>
      <AccordionItem css={classes.accordionItem} dangerouslySetExpanded={isAccordionDisabled ? false : undefined}>
        <HistoricoAccordionHeader
          historicoAtendimento={historicoAtendimento}
          subtipoAtendimento={subtipoAtendimento}
          refetchOnListagem={refetchOnListagem}
          cidadao={cidadao}
          checked={checked}
          onSelectToPrint={onSelectToPrint}
          historicoPrintProps={historicoPrintProps}
          isAccordionDisabled={isAccordionDisabled}
          isVisitaAusenteOuRecusada={isVisitaAusenteOuRecusada(historicoAtendimento)}
        />
        <AccordionItemState>
          {({ expanded }) => {
            if (expanded) {
              if (isAccordionClosedRef.current) {
                analytics.logEvent('expand_accordion_historico')
                isAccordionClosedRef.current = false
              }
              return (
                <AccordionItemPanel css={classes.accordionItemPanel}>
                  <VFlow vSpacing={1} style={classes.vFlow}>
                    <HistoricoDetalhesPanel
                      historicoAtendimento={historicoAtendimento}
                      idadeOrigemAtendimento={idadeOrigemAtendimento}
                      dataNascimento={cidadao.dataNascimento}
                    />
                  </VFlow>
                </AccordionItemPanel>
              )
            }
            isAccordionClosedRef.current = true
            return null
          }}
        </AccordionItemState>
      </AccordionItem>
    </HistoricoAccordionItemObservacaoWrapper>
  )
}

const styles = (theme: Theme, isAccordionDisabled: boolean) => {
  return {
    item: css`
      border-radius: 2px;
      border: 1px solid ${theme.pallete.primary.c40};
      background: ${theme.pallete.primary.c90};
      padding: 0.25rem 1rem;

      * {
        color: ${theme.pallete.primary.c40};
      }
    `,
    prependItem: css`
      border-bottom: 0;
    `,
    appendItem: css`
      border-top: 0;
    `,
    accordionItem: css`
      border: 1px solid ${theme.pallete.gray.c60};
      border-radius: 2px;
      &:focus-within {
        outline: none;
        ${!isAccordionDisabled
          ? `box-shadow: ${focusBoxShadow(theme, 'primary')};
            transition-property: box-shadow;
            transition-duration: 0.2s;`
          : ''}
      }
    `,
    accordionItemPanel: css`
      flex: 1;
      background-color: ${theme.pallete.surface.main};
    `,
    vFlow: css`
      padding: 1rem;
      padding-top: 0.5rem;
      overflow-wrap: break-word;
    `,
  }
}
